import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell, GoogleButton } from "@/pages/Login";
import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

export default function Register() {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (!agreed) return toast.error(t("privacy.consentError", "Please agree to the Privacy Policy to continue"));
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Your space is ready");
      navigate("/onboarding");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.createTitle")} subtitle={t("auth.createSub")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">{t("auth.callYou")}</Label>
          <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
                 className="mt-1.5 rounded-xl" data-testid="register-name-input" placeholder={t("auth.namePh")} />
        </div>
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="mt-1.5 rounded-xl" data-testid="register-email-input" placeholder="you@example.com" />
        </div>
        <div>
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                 className="mt-1.5 rounded-xl" data-testid="register-password-input" placeholder="At least 6 characters" />
        </div>
        <div className="flex items-start gap-2.5 pt-1">
          <Checkbox id="consent" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)}
                    className="mt-0.5" data-testid="register-consent-checkbox" />
          <Label htmlFor="consent" className="text-xs font-normal leading-relaxed text-muted-foreground">
            {t("privacy.consentPre")}{" "}
            <Link to="/privacy" target="_blank" className="text-primary hover:underline" data-testid="register-privacy-link">
              {t("privacy.policyLink")}
            </Link>.
          </Label>
        </div>
        <Button type="submit" disabled={loading || !agreed} className="w-full rounded-full" data-testid="register-submit-button">
          {loading ? t("auth.creating") : t("auth.createBtn")}
        </Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> {t("auth.or")} <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton testid="register-google-button" />
      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t("auth.haveAccount")} <Link to="/login" className="text-primary hover:underline" data-testid="go-login-link">{t("auth.signIn")}</Link>
      </p>
    </AuthShell>
  );
}
